import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const NavigationBar = ({ user, showLogo = true, cartCount = 0 }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  // Detect zoom level
  useEffect(() => {
    const detectZoom = () => {
      const zoom = Math.round((window.outerWidth / window.innerWidth) * 100) / 100;
      
      // Switch to hamburger menu on mobile or when zoomed
      // Mobile: < 1024px, Zoomed: > 110%
      const shouldShowMobileMenu = window.innerWidth < 1024 || zoom >= 1.1;
      setIsZoomed(shouldShowMobileMenu);
    };

    detectZoom();
    window.addEventListener('resize', detectZoom);
    
    // Also listen for zoom changes
    const mediaQuery = window.matchMedia('(min-resolution: 1.1dppx)');
    const handleZoomChange = () => detectZoom();
    mediaQuery.addEventListener('change', handleZoomChange);

    return () => {
      window.removeEventListener('resize', detectZoom);
      mediaQuery.removeEventListener('change', handleZoomChange);
    };
  }, []);

  return (
    <nav style={{ backgroundColor: '#FFFBF5' }} className="border-b border-black/10 fixed top-0 left-0 right-0 z-50 shadow-md">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12 py-3 md:py-4">
        <div className="flex items-center justify-between">
          {/* Left Side - Links - Hide on mobile */}
          <div className={`${isZoomed ? 'hidden' : 'hidden lg:flex'} items-center space-x-6 xl:space-x-10 text-xs lg:text-sm xl:text-base uppercase tracking-widest flex-1`}>
            <Link to="/about-us" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>About Us</Link>
            <Link to="/who-is-it-for" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>Who is it for</Link>
            <Link to="/how-it-works" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>How Does It Work</Link>
          </div>

          {/* Hamburger Menu Button - Show on mobile or zoomed */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className={`${isZoomed ? 'block' : 'lg:hidden'} flex-shrink-0 p-2 hover:bg-black/5 rounded transition-colors z-50`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Center - Logo */}
          <div className={`flex items-center justify-center flex-shrink-0 ${isZoomed ? 'absolute left-1/2 transform -translate-x-1/2' : 'mx-4 sm:mx-8 lg:mx-12'}`}>
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_hauteassets/artifacts/eca04feb_THC%20Final%20Logo%20%28Monogram%29.png" 
                alt="The Haute Circle" 
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain transition-opacity duration-500"
                style={{ opacity: showLogo ? 1 : 0 }}
              />
            </Link>
          </div>

          {/* Right Side - Links & Login - Hide on mobile */}
          <div className={`${isZoomed ? 'hidden' : 'hidden lg:flex'} items-center space-x-6 xl:space-x-10 text-xs lg:text-sm xl:text-base uppercase tracking-widest flex-1 justify-end`}>
            <Link to="/high-assets" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>High Assets</Link>
            <Link to="/high-luxury" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>High Luxury</Link>
            <Link to="/high-life" className="font-normal whitespace-nowrap transition-colors" style={{ color: '#000000' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#000000'}>High Lifestyle</Link>
            {user ? (
              <Link to="/member-dashboard" className="relative">
                <Button className="bg-gold hover:bg-gold/90 text-black text-xs lg:text-sm xl:text-base uppercase px-3 lg:px-4 xl:px-5 py-1 rounded whitespace-nowrap transition-all" data-testid="dashboard-btn">
                  Dashboard
                </Button>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-gold hover:bg-gold/90 text-black text-xs lg:text-sm xl:text-base uppercase px-3 lg:px-4 xl:px-5 py-1 rounded whitespace-nowrap transition-all" data-testid="login-btn">Login</Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile/Zoomed Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mt-4 pb-4 space-y-2 border-t border-black/10 pt-4 animate-in slide-in-from-top duration-300">
            <Link 
              to="/about-us" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/who-is-it-for" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Who is it for
            </Link>
            <Link 
              to="/how-it-works" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              How Does It Work
            </Link>
            <Link 
              to="/high-assets" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              High Assets
            </Link>
            <Link 
              to="/high-luxury" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              High Luxury
            </Link>
            <Link 
              to="/high-life" 
              className="block text-black hover:text-gold font-normal uppercase tracking-widest py-3 px-4 hover:bg-black/5 rounded transition-all text-sm visited:text-black active:text-black focus:text-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              High Lifestyle
            </Link>
            {user ? (
              <Link 
                to="/member-dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="relative block"
              >
                <Button className="w-full bg-gold hover:bg-gold/90 text-black uppercase tracking-widest text-sm mt-2 relative" data-testid="dashboard-btn-mobile">
                  Dashboard
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-black text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>
            ) : (
              <Link 
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button className="w-full bg-gold hover:bg-gold/90 text-black uppercase tracking-widest text-sm mt-2" data-testid="login-btn-mobile">Login</Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationBar;